import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../../components/ui/Input';
import Select from '../../../../components/ui/Select';
import { DatePicker as CustomDatePicker } from '../../../../components/dateTimePickers';
import { formatDateForInput } from '../../../../utils';
import { getCompanyAttachmentTypes } from '../../../../api/General/attachment';
import { CompanyAttachmentTypeView, FileAnalysisResult } from '../../../../models/general';
import OcrConfidenceBadge from './OcrConfidenceBadge';

interface FormData {
  fileName: string;
  description: string;
  expiryDate?: string;
  reminderDays?: number;
  companyAttachmentTypeId?: number;
  documentNumber?: string;
  issueDate?: string;
}

interface DocumentFormPanelProps {
  formData: FormData;
  onChange: (data: FormData) => void;
  ocrResult: FileAnalysisResult | null;
  analyzing: boolean;
}

const DocumentFormPanel: React.FC<DocumentFormPanelProps> = ({
  formData,
  onChange,
  ocrResult,
  analyzing,
}) => {
  const { t } = useTranslation();
  const [attachmentTypes, setAttachmentTypes] = useState<CompanyAttachmentTypeView[]>([]);

  useEffect(() => {
    const fetchTypes = async () => {
      const response = await getCompanyAttachmentTypes();
      setAttachmentTypes(response.payload || []);
    };
    fetchTypes();
  }, []);

  // Auto-fill from OCR results
  useEffect(() => {
    if (!ocrResult || !ocrResult.ocrSuccess) return;

    const updates: Partial<FormData> = {};

    if (ocrResult.matchedAttachmentTypeId && !formData.companyAttachmentTypeId) {
      updates.companyAttachmentTypeId = ocrResult.matchedAttachmentTypeId;
      // Also set reminder days from matched type
      const matchedType = attachmentTypes.find(at => at.id === ocrResult.matchedAttachmentTypeId);
      if (matchedType) {
        updates.reminderDays = matchedType.reminderDays;
      }
    }

    if (ocrResult.detectedExpiryDate && !formData.expiryDate) {
      updates.expiryDate = ocrResult.detectedExpiryDate;
    }

    if (Object.keys(updates).length > 0) {
      onChange({ ...formData, ...updates });
    }
  }, [ocrResult, attachmentTypes]);

  const skeleton = (
    <div className="h-9 animate-pulse rounded-md bg-muted" />
  );

  return (
    <form className="flex flex-col gap-4" onSubmit={(e) => e.preventDefault()}>
      {/* Document Type */}
      <div>
        <div className="flex items-center gap-2">
          <div className="flex-1">
            {analyzing ? skeleton : (
              <Select
                label={t('Document Type')}
                options={attachmentTypes.map(at => ({ label: at.attachmentType, value: at.id }))}
                value={formData.companyAttachmentTypeId}
                onChange={(v: number) => {
                  const selected = attachmentTypes.find(at => at.id === v);
                  onChange({
                    ...formData,
                    companyAttachmentTypeId: v,
                    reminderDays: selected?.reminderDays ?? formData.reminderDays,
                  });
                }}
                searchable
              />
            )}
          </div>
          {ocrResult?.ocrSuccess && ocrResult.documentTypeConfidence > 0 && !analyzing && (
            <OcrConfidenceBadge confidence={ocrResult.documentTypeConfidence} className="mt-5" />
          )}
        </div>
        {ocrResult?.ocrSuccess && ocrResult.detectedDocumentType && !analyzing && (
          <p className="mt-1 text-xs text-muted-foreground">
            {t('Detected')}: {ocrResult.detectedDocumentType}
          </p>
        )}
      </div>

      {/* Document Number */}
      <Input
        label={t('documentNumber')}
        type="text"
        value={formData.documentNumber ?? ''}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...formData, documentNumber: e.target.value })}
      />

      {/* File Name */}
      <Input
        label={t('File Name')}
        type="text"
        value={formData.fileName}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...formData, fileName: e.target.value })}
      />

      {/* Description */}
      <Input
        label={t('Description')}
        type="text"
        value={formData.description}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...formData, description: e.target.value })}
      />

      {/* Issue Date */}
      <CustomDatePicker
        label={t('issueDate')}
        value={formData.issueDate ? new Date(formData.issueDate) : null}
        onChange={(date: Date | null) => onChange({ ...formData, issueDate: date ? formatDateForInput(date) : undefined })}
      />

      {/* Expiry Date */}
      <div>
        <div className="flex items-center gap-2">
          <div className="flex-1">
            {analyzing ? skeleton : (
              <CustomDatePicker
                label={t('Expiry Date')}
                value={formData.expiryDate ? new Date(formData.expiryDate) : null}
                onChange={(date: Date | null) => onChange({ ...formData, expiryDate: date ? formatDateForInput(date) : undefined })}
              />
            )}
          </div>
          {ocrResult?.ocrSuccess && ocrResult.expiryDateConfidence > 0 && !analyzing && (
            <OcrConfidenceBadge confidence={ocrResult.expiryDateConfidence} className="mt-5" />
          )}
        </div>
      </div>

      {/* Reminder Days */}
      <Input
        label={t('Reminder Days')}
        type="number"
        value={formData.reminderDays ?? ''}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...formData, reminderDays: parseInt(e.target.value) || undefined })}
      />

      {/* Analyzing indicator */}
      {analyzing && (
        <div className="flex items-center gap-2 rounded-lg bg-primary/5 p-3">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <span className="text-sm text-primary">{t('Analyzing document...')}</span>
        </div>
      )}

      {/* OCR error */}
      {ocrResult && !ocrResult.ocrSuccess && ocrResult.errorMessage && (
        <p className="text-xs text-muted-foreground">{ocrResult.errorMessage}</p>
      )}
    </form>
  );
};

export default DocumentFormPanel;
